import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  assignPackage,
  extractText,
  getAssignState,
  getFileError,
  rematchNotes,
  saveNotes,
} from './ocrActions';

const PREVIEWABLE = /^image\/(jpeg|png)$/;

// Upload, drag-and-drop and Ctrl+V screenshots all end up in `file`.
export const useOcrFile = (show, resetKey) => {
  const [file, setFile] = useState(null);

  const selectFile = useCallback((selected) => {
    if (!selected) return;
    const error = getFileError(selected);
    if (error) {
      toast.error(error);
      return;
    }
    setFile(selected);
  }, []);

  useEffect(() => {
    if (show) setFile(null);
  }, [show, resetKey]);

  useEffect(() => {
    if (!show) return undefined;
    const handlePaste = (e) => {
      const pasted = Array.from(e.clipboardData?.files || []).find((f) => f.type.startsWith('image/'));
      selectFile(pasted);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [show, selectFile]);

  const previewUrl = useMemo(() => (file && PREVIEWABLE.test(file.type) ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const handleDrop = (e) => {
    e.preventDefault();
    selectFile(e.dataTransfer?.files?.[0]);
  };

  return { file, previewUrl, selectFile, handleDrop };
};

export const useOcrSession = ({ show, row, file, onHide, onAssigned }) => {
  const [useExistingImage, setUseExistingImage] = useState(true);
  const [notes, setNotes] = useState('');
  const [match, setMatch] = useState(null);
  const [matchedNotes, setMatchedNotes] = useState('');
  const [seller, setSeller] = useState({ companyId: null, sellerId: null });
  const [busy, setBusy] = useState('');
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (!show) return;
    setUseExistingImage(Boolean(row?.imageUrl || row?.thumbnailUrl));
    setNotes('');
    setMatch(null);
    setMatchedNotes('');
    setSeller({ companyId: null, sellerId: null });
    setBusy('');
    dirtyRef.current = false;
  }, [show, row?.PackageId]);

  const ctx = {
    row, file, notes, match, seller, useExistingImage, setNotes, setMatch, setMatchedNotes, onAssigned,
    markDirty: () => { dirtyRef.current = true; },
    onHide,
  };

  const run = (key, action) => async () => {
    setBusy(key);
    try {
      await action(ctx);
    } finally {
      setBusy('');
    }
  };

  const handleClose = () => {
    if (!busy) onHide(dirtyRef.current);
  };

  return {
    useExistingImage, setUseExistingImage, notes, setNotes, match, seller, setSeller, busy,
    assignState: getAssignState({ match, notes, matchedNotes, seller }),
    handleClose,
    handleExtract: run('extract', extractText),
    handleRematch: run('match', rematchNotes),
    handleSaveNotes: run('save', saveNotes),
    handleAssign: run('assign', assignPackage),
  };
};
